import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { mergeReducedMotionPreset, resolveExpandTransition, useThemeMotion } from '../../animation/themeMotion'
import { useTheme } from '../../theme/ThemeContext'
import { chromeToolbarButtonClass } from '../ChromeSurface'

export type AgentInlineOption = {
  value: string
  label: string
  description?: string
  meta?: string
}

type AgentInlinePickerProps = {
  value: string
  options: AgentInlineOption[]
  onChange: (value: string) => void
  disabled?: boolean
  ariaLabel?: string
  menuTitle?: string
  align?: 'left' | 'right'
  minMenuWidth?: number
  /** 多选模式：点击选项只切换勾选、不关闭菜单；value 仅作触发器兜底文案 */
  multiSelect?: boolean
  /** 多选模式下已勾选的值集合 */
  selectedValues?: string[]
  /** 多选模式下点击某项的切换回调 */
  onToggle?: (value: string) => void
  /** 多选模式下触发器显示的文案（如「工具 5/8」），缺省回落到选中项 label */
  triggerLabel?: string
}

type MenuLayout = {
  bottom: number
  left: number
  width: number
  maxHeight: number
}

const VIEWPORT_MARGIN = 8
const GAP = 8

export function AgentInlinePicker({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel = '选项',
  menuTitle,
  align = 'left',
  minMenuWidth = 240,
  multiSelect = false,
  selectedValues,
  onToggle,
  triggerLabel,
}: AgentInlinePickerProps) {
  const autoId = useId()
  const listId = `${autoId}-list`
  const btnId = `${autoId}-btn`
  const [open, setOpen] = useState(false)
  const [menuLayout, setMenuLayout] = useState<MenuLayout | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const ignoreTriggerClickRef = useRef(false)
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const dropdownTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  const selected = useMemo(
    () => options.find((item) => item.value === value) ?? options[0],
    [options, value],
  )

  const syncPosition = () => {
    const el = rootRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const width = Math.min(Math.max(rect.width, minMenuWidth), Math.min(360, vw - 2 * VIEWPORT_MARGIN))
    const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN
    const maxHeight = Math.min(320, Math.max(160, spaceAbove))
    let left = align === 'right' ? rect.right - width : rect.left
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN
    if (left + width > vw - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - width)
    }
    setMenuLayout({
      bottom: vh - rect.top + GAP,
      left,
      width,
      maxHeight,
    })
  }

  useLayoutEffect(() => {
    if (!open) {
      setMenuLayout(null)
      return
    }
    syncPosition()
    const onResize = () => syncPosition()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [open, options.length, align, minMenuWidth])

  useEffect(() => {
    if (!open) return
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return
      ignoreTriggerClickRef.current = true
      setOpen(false)
      window.setTimeout(() => {
        ignoreTriggerClickRef.current = false
      }, 0)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDoc, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  const menu = open && menuLayout ? (
    <motion.div
      ref={listRef}
      id={listId}
      role="listbox"
      aria-labelledby={btnId}
      className={[
        'overflow-hidden bg-[color-mix(in_srgb,var(--menu-solid)_96%,transparent)] shadow-float ring-1 ring-[color-mix(in_srgb,var(--line)_35%,transparent)]',
        angular ? 'rounded-none' : 'rounded-2xl',
      ].join(' ')}
      style={{
        position: 'fixed',
        bottom: menuLayout.bottom,
        left: menuLayout.left,
        width: menuLayout.width,
        maxHeight: menuLayout.maxHeight,
        zIndex: 120000,
      }}
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.99 }}
      transition={dropdownTrans}
    >
      {menuTitle ? (
        <div className="px-3 py-2.5">
          <p className="text-[11px] font-medium tracking-[0.08em] text-ink-soft">{menuTitle}</p>
        </div>
      ) : null}
      <div className="scroll-region max-h-[16rem] overflow-y-auto p-1.5">
        {options.map((option) => {
          const active = multiSelect
            ? (selectedValues?.includes(option.value) ?? false)
            : option.value === value
          return (
            <button
              key={option.value || 'default'}
              type="button"
              role="option"
              aria-selected={active}
              className={[
                'flex w-full items-center gap-3 px-3 py-2.5 text-left transition',
                angular ? 'rounded-none' : 'rounded-xl',
                active && !multiSelect
                  ? 'bg-[var(--segment-active-bg)] text-[var(--segment-active-fg)]'
                  : 'text-ink hover:bg-surface-muted/80',
              ].join(' ')}
              onClick={() => {
                if (multiSelect) {
                  onToggle?.(option.value)
                  return
                }
                onChange(option.value)
                setOpen(false)
              }}
            >
              {multiSelect ? (
                <span
                  aria-hidden
                  className={[
                    'flex h-4 w-4 shrink-0 items-center justify-center border',
                    angular ? 'rounded-none' : 'rounded-[5px]',
                    active
                      ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                      : 'border-[color-mix(in_srgb,var(--line)_60%,transparent)] bg-transparent',
                  ].join(' ')}
                >
                  {active ? (
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  ) : null}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{option.label}</span>
                {option.description ? (
                  <span
                    className={[
                      'mt-0.5 block truncate text-xs',
                      active ? 'opacity-80' : 'text-ink-soft',
                    ].join(' ')}
                  >
                    {option.description}
                  </span>
                ) : null}
              </span>
              {option.meta ? (
                <span
                  className={[
                    'shrink-0 text-[11px]',
                    active ? 'opacity-80' : 'text-ink-soft',
                  ].join(' ')}
                >
                  {option.meta}
                </span>
              ) : null}
              {active ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0"
                  aria-hidden
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : null}
            </button>
          )
        })}
      </div>
    </motion.div>
  ) : null

  return (
    <>
      <div ref={rootRef} className="relative min-w-0">
        <button
          id={btnId}
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listId}
          disabled={disabled}
          onClick={() => {
            if (ignoreTriggerClickRef.current) {
              ignoreTriggerClickRef.current = false
              return
            }
            if (!disabled) setOpen((prev) => !prev)
          }}
          className={[
            chromeToolbarButtonClass(angular),
            'inline-flex max-w-full items-center gap-1.5',
            disabled ? 'pointer-events-none opacity-45' : '',
          ].join(' ')}
        >
          <span className="min-w-0 truncate font-medium">
            {multiSelect ? (triggerLabel ?? selected?.label ?? '未选择') : (selected?.label ?? '未选择')}
          </span>
          <motion.svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-ink-soft"
            aria-hidden
            animate={{ rotate: open ? 180 : 0 }}
            transition={dropdownTrans}
          >
            <path d="m6 9 6 6 6-6" />
          </motion.svg>
        </button>
      </div>
      {typeof document !== 'undefined'
        ? createPortal(<AnimatePresence>{menu}</AnimatePresence>, document.body)
        : null}
    </>
  )
}

/** @deprecated 使用 AgentInlinePicker */
export const AgentModelPicker = AgentInlinePicker
export type AgentModelOption = AgentInlineOption
